import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, CheckCircle2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PROGRAMS, SUBJECTS } from "@/lib/content";
import useSEO from "@/hooks/useSEO";

const UNIFIED_TIMING = ["All Batches · 7:00 AM – 9:00 PM"];

const TIMINGS = {
  main: UNIFIED_TIMING,
  senior: UNIFIED_TIMING,
  foundation: UNIFIED_TIMING,
};

const COURSES_DETAIL = [
  {
    id: "main",
    title: "Main Section · 4th to 10th",
    intro: "Comprehensive coaching across CBSE, ICSE and Maharashtra State Board. Every subject, regular tests, and strong conceptual clarity.",
    items: [
      "All school subjects covered",
      "Board-aligned syllabus (CBSE · ICSE · State)",
      "Weekly chapter tests & PTA reports",
      "10th Board crash revision",
      "Doubt clearance hours",
    ],
    timings: TIMINGS.main,
  },
  {
    id: "senior",
    title: "Senior Section · 11th & 12th Science",
    intro: "Integrated Board + Entrance prep. Dedicated IIT-JEE, NEET and MHT-CET tracks with expert faculty and benchmark test series.",
    items: [
      "PCM / PCB covered in depth",
      "IIT-JEE · NEET · MHT-CET prep",
      "Weekly full-length tests",
      "All-India benchmark analysis",
      "Personal mentor assigned",
    ],
    timings: TIMINGS.senior,
  },
  {
    id: "foundation",
    title: "Foundation Course with IIT-JEE | NEET Preparation",
    intro: "Early exposure to competitive reasoning, IIT-JEE and NEET aligned foundation for 8th – 10th students. Builds concept strength, aptitude and subject depth from the ground up.",
    items: [
      "IIT-JEE aligned foundation (Maths · Physics)",
      "NEET aligned foundation (Biology · Chemistry)",
      "NTSE / Olympiad readiness",
      "Logical reasoning & Aptitude",
      "Smooth transition to 11th Science",
    ],
    timings: TIMINGS.foundation,
  },
];

export default function Courses() {
  useSEO({
    title: "Courses — JEE, NEET, MH-CET & Foundation | Kothari Classes Jalgaon",
    description: "Structured courses from Kids (Nursery–3rd) to 12th Science with JEE, NEET, MH-CET tracks at Kothari Classes, Jalgaon. All batches 7:00 AM – 9:00 PM.",
    canonical: "https://www.kothariclasses.in/courses",
  });
  return (
    <div data-testid="courses-page">
      {/* Header */}
      <section className="bg-gray-950 text-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-bold tracking-[0.22em] text-red-500 uppercase">Courses</span>
            <h1 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1]">
              Every stage,<br/>every subject, one standard.
            </h1>
            <p className="mt-5 text-gray-400 text-lg max-w-xl">
              Structured programs from 4th to 12th plus dedicated IIT-JEE · NEET · MHT-CET tracks. Choose what fits your child's journey.
            </p>
          </div>
        </div>
      </section>

      {/* Tabs / Programs */}
      <section className="bg-white py-16 lg:py-24" data-testid="courses-tabs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="main" className="w-full">
            <TabsList className="w-full flex overflow-x-auto no-scrollbar h-auto p-0 bg-transparent justify-start gap-0 border-b border-gray-200 rounded-none">
              {PROGRAMS.map((p) => (
                <TabsTrigger
                  key={p.key}
                  value={p.key}
                  data-testid={`courses-tab-${p.key}`}
                  className="rounded-none px-5 py-3 text-sm font-semibold text-gray-500 data-[state=active]:text-gray-950 data-[state=active]:border-b-2 data-[state=active]:border-red-600 data-[state=active]:shadow-none data-[state=active]:bg-transparent bg-transparent transition-colors whitespace-nowrap"
                >
                  {p.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {PROGRAMS.map((p) => {
              const detail = COURSES_DETAIL.find((c) => c.id === p.key);
              return (
                <TabsContent key={p.key} value={p.key} className="mt-10" data-testid={`courses-tab-content-${p.key}`}>
                  <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                      <div className="relative">
                        <div className="absolute -top-3 -left-3 w-full h-full border-2 border-red-600" />
                        <img src={p.image} alt={p.label} className="relative w-full h-[380px] object-cover" />
                      </div>
                    </div>
                    <div className="lg:col-span-7 space-y-6">
                      <div>
                        <span className="eyebrow">{p.range}</span>
                        <h2 className="font-display text-3xl lg:text-4xl font-black text-gray-950 mt-2">{detail?.title}</h2>
                      </div>
                      <p className="text-gray-600 text-lg leading-relaxed">{detail?.intro}</p>
                      <ul className="space-y-2.5">
                        {detail?.items.map((it) => (
                          <li key={it} className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                            <span className="text-gray-800">{it}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="border border-gray-200 p-5 bg-gray-50">
                        <div className="flex items-center gap-2 mb-3">
                          <Clock className="w-4 h-4 text-red-600" />
                          <span className="text-xs font-bold uppercase tracking-widest text-gray-900">Batch Timings</span>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-2 text-sm text-gray-700">
                          {detail?.timings.map((t) => <div key={t} className="flex gap-2"><span className="text-red-600">·</span>{t}</div>)}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3 pt-2">
                        <Link to="/admissions" className="btn-primary rounded-none text-sm" data-testid={`courses-enroll-${p.key}`}>
                          Enroll Now <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link to="/admissions" className="btn-secondary rounded-none text-sm" data-testid={`courses-demo-${p.key}`}>
                          Book Free Demo
                        </Link>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </section>

      {/* Subjects */}
      <section className="bg-gray-50 py-20" data-testid="courses-subjects">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <span className="eyebrow">Curriculum</span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-black tracking-tight text-gray-950">12 subjects. Rigorously taught.</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {SUBJECTS.map((s, i) => (
              <div key={s.name} className="border border-gray-300 bg-white py-4 px-5 text-sm font-semibold text-gray-900 hover:border-red-600 hover:text-red-600 transition-colors" data-testid={`courses-subject-${i}`}>
                {s.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="bg-white py-20" data-testid="courses-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <span className="eyebrow">Frequently asked</span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-black tracking-tight text-gray-950">Questions parents ask us</h2>
          </div>
          <Accordion type="single" collapsible className="w-full border-t border-gray-200">
            {[
              { q: "What boards do you cover?", a: "CBSE, ICSE and Maharashtra State Board — all in structured, board-specific batches." },
              { q: "How large are the batches?", a: "Batches are kept between 30–40 students so every student gets focused attention from our faculty." },
              { q: "Do you offer a free demo class?", a: "Yes — you can attend a full demo class before enrolling. Book via the Admissions page or WhatsApp." },
              { q: "When do admissions close?", a: "Admissions for 2026–27 are open on a rolling basis; seats are limited per batch." },
              { q: "Is there a registration discount?", a: "Yes, an early-bird discount of up to 16.3% is available for confirmed admissions this quarter." },
            ].map((f, i) => (
              <AccordionItem key={i} value={`q${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="font-display text-lg font-semibold text-gray-950 hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 text-base leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
